import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Music, Mic, Image as ImageIcon, Video, KeyRound, Check, AlertCircle, Zap, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useCurrency } from "@/lib/currency";

/**
 * UsageBanner — Update #97.
 *
 * Compact dashboard widget showing this month's BYOK free-tier usage
 * across the 5 spec providers (ElevenLabs / Suno / Mubert / D-ID / Unsplash).
 *
 * Per provider:
 *   • Connected own key → green "BYOK" badge, no meter
 *   • Free tier with usage → meter (green <70%, amber <90%, rose ≥90%)
 *   • Capped / no platform key → rose "Connect key" CTA
 *
 * + Upsell strip below the cards: surfaces a Stripe one-click checkout
 *   for the highest-usage provider whenever any meter crosses 80%.
 *
 * Refreshes on mount + every 60s. Hidden when the API errors (don't block
 * the dashboard for a non-critical widget).
 */
const ICON_BY_CATEGORY = {
  voice: Mic,
  music: Music,
  image: ImageIcon,
  video: Video,
};

const UPSELL_PCT_THRESHOLD = 80;

function meterColor(pct, capped) {
  if (capped) return "from-rose-500 to-rose-400";
  if (pct >= 90) return "from-rose-400 to-amber-400";
  if (pct >= 70) return "from-amber-400 to-amber-300";
  return "from-emerald-500 to-emerald-400";
}

/** Returns the most-burned-down non-BYOK provider that crossed the upsell
 * threshold, or null if no upsell is currently warranted. */
function pickUpsellCandidate(items) {
  const candidates = items
    .filter((i) => !i.byok && i.topup_pack && (i.pct || 0) >= UPSELL_PCT_THRESHOLD)
    .sort((a, b) => (b.pct || 0) - (a.pct || 0));
  return candidates[0] || null;
}

export default function UsageBanner() {
  const { fmt: fx } = useCurrency();
  const [snap, setSnap] = useState(null);
  const [err, setErr] = useState(false);
  const [buying, setBuying] = useState(null); // sku currently checking out
  const location = useLocation();
  const navigate = useNavigate();

  const fetchSnap = async () => {
    try {
      const r = await api.get("/byok/usage");
      setSnap(r.data);
      setErr(false);
    } catch {
      setErr(true);
    }
  };

  useEffect(() => {
    let alive = true;
    const tick = async () => { if (alive) await fetchSnap(); };
    tick();
    const t = setInterval(tick, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Stripe success-redirect handler — confirm credits arrived + flash toast
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const topup = params.get("topup");
    if (topup === "success") {
      const provider = params.get("provider") || "";
      // Poll up to 3 times (5s apart) for the webhook to land
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        await fetchSnap();
        if (attempts < 3) setTimeout(poll, 5000);
      };
      toast.success(
        `Top-up applied${provider ? ` for ${provider}` : ""} — credits will appear within a few seconds.`,
        { duration: 6000 }
      );
      poll();
      // Strip query so the toast doesn't refire on navigation
      navigate({ pathname: location.pathname, search: "" }, { replace: true });
    } else if (topup === "cancelled") {
      toast.info("Top-up cancelled. No charge was made.");
      navigate({ pathname: location.pathname, search: "" }, { replace: true });
    }
  }, [location.search]);

  const startCheckout = async (sku) => {
    if (!sku || buying) return;
    setBuying(sku);
    try {
      const r = await api.post("/byok/topup/checkout", { sku });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.error("Couldn't start checkout — please try again.");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Checkout failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Checkout failed.");
    } finally {
      setBuying(null);
    }
  };

  if (err || !snap || !snap.items?.length) return null;

  const cappedCount = snap.items.filter((i) => i.capped).length;
  const byokCount = snap.items.filter((i) => i.byok).length;
  const upsell = pickUpsellCandidate(snap.items);

  return (
    <div
      className="rounded-xl border border-line bg-bg-surface p-5"
      data-testid="usage-banner"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="rounded-md bg-amber-500/10 p-1.5">
            <Sparkles className="h-4 w-4 text-amber-400" />
          </div>
          <div>
            <h3 className="font-display text-base font-bold leading-tight">AI Provider Usage</h3>
            <p className="text-[11px] text-ink-muted">
              {snap.month && `${snap.month} · `}
              Resets in {snap.resets_in_days || 0} day{(snap.resets_in_days || 0) === 1 ? "" : "s"}
              {byokCount > 0 && ` · ${byokCount} BYOK connected`}
              {cappedCount > 0 && ` · ${cappedCount} capped`}
            </p>
          </div>
        </div>
        <Link
          to="/dashboard/ai-providers"
          className="text-xs font-semibold text-amber-400 hover:underline flex items-center gap-1"
          data-testid="usage-banner-manage-link"
        >
          Manage keys →
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {snap.items.map((it) => {
          const Icon = ICON_BY_CATEGORY[it.category] || Sparkles;
          const pct = Math.min(100, Number(it.pct) || 0);
          const isCapped = it.capped && !it.byok;
          return (
            <div
              key={it.provider}
              className="rounded-lg border border-line bg-bg-elevated/50 p-3"
              data-testid={`usage-card-${it.provider}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <Icon className="h-3.5 w-3.5 text-ink-muted" />
                  <span className="text-xs font-semibold">{it.label}</span>
                </div>
                {it.byok ? (
                  <span
                    className="flex items-center gap-0.5 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                    data-testid={`usage-badge-byok-${it.provider}`}
                  >
                    <Check className="h-2.5 w-2.5" />
                    BYOK
                  </span>
                ) : isCapped ? (
                  <span
                    className="flex items-center gap-0.5 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-400 border border-rose-500/30"
                    data-testid={`usage-badge-capped-${it.provider}`}
                  >
                    <AlertCircle className="h-2.5 w-2.5" />
                    Capped
                  </span>
                ) : null}
              </div>

              {it.byok ? (
                <div className="text-[11px] text-emerald-400/80 leading-snug">
                  Your key — unlimited
                </div>
              ) : (
                <>
                  <div className="flex items-baseline justify-between mb-1.5">
                    <span className="font-display text-lg font-bold leading-none">
                      {it.used}
                      <span className="text-xs text-ink-muted font-normal">/{it.limit}</span>
                    </span>
                    <span className="text-[10px] text-ink-muted">{pct.toFixed(0)}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-bg-base overflow-hidden">
                    <div
                      className={`h-full bg-gradient-to-r ${meterColor(pct, isCapped)} transition-all`}
                      style={{ width: `${pct}%` }}
                      data-testid={`usage-meter-${it.provider}`}
                    />
                  </div>
                  {it.bonus > 0 && (
                    <div className="mt-1 text-[10px] text-emerald-400/80 leading-tight">
                      +{it.bonus} paid top-up
                    </div>
                  )}
                  {isCapped && (
                    <Link
                      to="/dashboard/ai-providers"
                      className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-400 hover:underline"
                      data-testid={`usage-connect-key-${it.provider}`}
                    >
                      <KeyRound className="h-2.5 w-2.5" />
                      Connect key
                    </Link>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Upsell strip — only shows when ≥1 meter has crossed the threshold */}
      {upsell && upsell.topup_pack && (
        <div
          className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 rounded-lg border border-amber-500/40 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent p-3"
          data-testid="usage-upsell-strip"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="rounded-full bg-amber-500/15 p-2 border border-amber-500/30 flex-shrink-0">
              <Zap className="h-4 w-4 text-amber-400" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold leading-tight" data-testid="usage-upsell-title">
                {upsell.used}/{upsell.limit} {upsell.label} used —{" "}
                <span className="text-amber-400">{(upsell.pct || 0).toFixed(0)}%</span>
              </div>
              <div className="text-[11px] text-ink-muted leading-tight mt-0.5">
                {upsell.topup_pack.label} · {fx(upsell.topup_pack.price_usd, { decimals: 2 })} one-time · no subscription
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link
              to="/dashboard/ai-providers"
              className="text-xs font-semibold text-ink-muted hover:text-ink-base transition-colors px-3 py-2"
              data-testid="usage-upsell-byok-link"
            >
              Connect key instead
            </Link>
            <button
              type="button"
              onClick={() => startCheckout(upsell.topup_pack.sku)}
              disabled={buying === upsell.topup_pack.sku}
              data-testid="usage-upsell-buy-btn"
              className="rounded-md bg-amber-500 hover:bg-amber-400 disabled:opacity-60 disabled:cursor-wait text-bg-base font-bold px-4 py-2 text-xs flex items-center gap-2 transition-colors"
            >
              {buying === upsell.topup_pack.sku ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Redirecting…
                </>
              ) : (
                <>
                  <Zap className="h-3.5 w-3.5" />
                  Top up {fx(upsell.topup_pack.price_usd, { decimals: 2 })}
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
